import React from 'react';
import { useNavigate } from 'react-router-dom';
import freshmarkVideo from '../assets/001.mp4';
import analyticsDashboardVideo from '../assets/Analytics dashboard.mp4';
import productManagementVideo from '../assets/Product management.mp4';
import storeManagementVideo from '../assets/Store management.mp4';
import labelTemplatesVideo from '../assets/Label templates.mp4';
import storeSelectionVideo from '../assets/Store selection.MP4';
import printingFlowVideo from '../assets/Printing flow.mov';
import developmentShowcaseImage from '../assets/development01.jpg';
import { projectDetailThemeStyles } from '../projectThemes';
import './ProjectDetailLayout.css';
import './FreshMarkProject.css';

export const FreshMarkProject: React.FC = () => {
  const navigate = useNavigate();
  return (
  <main
    className="project-detail-page freshmark-project-page"
    style={projectDetailThemeStyles.freshmark}
  >
    <aside className="project-page__sidebar">
      <button type="button" className="project-page__back" onClick={() => navigate(-1)}>
        <span className="project-page__back-arrow" aria-hidden="true">
          &lt;-
        </span>
        back to showcase
      </button>
      <div className="project-page__sidebar-summary">
        <p className="project-page__eyebrow">development work / product system</p>
        <h1
          className="project-page__title"
          aria-label="FreshMark: Sushi Label Management System"
        >
          FreshMark
        </h1>
        <p className="project-page__description">
          One label workflow connecting sushi-store operations, in-store printing, and HQ
          reporting.
        </p>
        <div className="project-page__tech-list" aria-label="Technologies used">
          <span>React</span>
          <span>Node.js</span>
          <span>PostgreSQL</span>
          <span>SwiftUI</span>
        </div>
      </div>
    </aside>

    <section className="project-page__content" aria-label="Project content">
      <div className="project-page__section">
        <p className="project-page__section-kicker">overview / system design</p>
        <h2 className="project-page__section-title">One workflow, from HQ to every store.</h2>
        <div className="project-page__section-body">
          <p>
            FreshMark is an end‑to‑end label management and printing system for sushi stores,
            consisting of a web admin console and an iOS store app. It connects product and store
            data management, label template design, in‑store printing, and print‑data reporting
            into one workflow to improve speed and consistency.
          </p>
          <figure className="project-page__inline-figure freshmark-project__overview-figure">
            <img
              className="project-page__inline-image"
              src={developmentShowcaseImage}
              alt="FreshMark label-management dashboard and iOS printing workflow"
            />
          </figure>

          <h3 className="freshmark-project__section-heading">Admin Web (Management Console)</h3>
          <ul className="freshmark-project__list">
            <li>
              <span className="freshmark-project__list-label">Analytics dashboard</span>
              Track daily active stores, total labels, first‑print time, and late stores, with
              filters by date, region, and store, plus export.
            </li>
            <li className="freshmark-project__list-media">
              <video
                className="freshmark-project__inline-video"
                src={analyticsDashboardVideo}
                autoPlay
                loop
                muted
                playsInline
              />
            </li>
            <li>
              <span className="freshmark-project__list-label">Data Library</span>
              Supports store-by-store and date-range comparison charts to help HQ identify
              performance gaps.
            </li>
            <li className="freshmark-project__list-media">
              <video
                className="freshmark-project__inline-video"
                src={freshmarkVideo}
                autoPlay
                loop
                muted
                playsInline
              />
            </li>
            <li>
              <span className="freshmark-project__list-label">Product management</span>
              Maintain product name, category, shelf life, barcode, weight, price, ingredients,
              allergens, and nutrition information, with support for custom fields.
            </li>
            <li className="freshmark-project__list-media">
              <video
                className="freshmark-project__inline-video"
                src={productManagementVideo}
                autoPlay
                loop
                muted
                playsInline
              />
            </li>
            <li>
              <span className="freshmark-project__list-label">Store management</span>
              Maintain store code, name, region, address, and coordinates to enable
              location‑based selection on the app.
            </li>
            <li className="freshmark-project__list-media">
              <video
                className="freshmark-project__inline-video"
                src={storeManagementVideo}
                autoPlay
                loop
                muted
                playsInline
              />
            </li>
            <li>
              <span className="freshmark-project__list-label">Label templates</span>
              Drag‑and‑drop layout of fields, fonts, alignment, and spacing; supports image
              upload/cropping, preview, and publishing to stores.
            </li>
            <li className="freshmark-project__list-media">
              <video
                className="freshmark-project__inline-video"
                src={labelTemplatesVideo}
                autoPlay
                loop
                muted
                playsInline
              />
            </li>
          </ul>

          <h3 className="freshmark-project__section-heading">Mobile App (Store‑side iOS)</h3>
          <ul className="freshmark-project__list">
            <li>
              <span className="freshmark-project__list-label">Store selection</span>
              Location‑aware nearby stores plus search.
            </li>
            <li className="freshmark-project__list-media">
              <video
                className="freshmark-project__inline-video freshmark-project__inline-video--mobile"
                src={storeSelectionVideo}
                autoPlay
                loop
                muted
                playsInline
              />
            </li>
            <li>
              <span className="freshmark-project__list-label">Printing flow</span>
              Select products and quantities, auto‑match templates, enter staff name, and print.
            </li>
            <li className="freshmark-project__list-media">
              <video
                className="freshmark-project__inline-video freshmark-project__inline-video--mobile"
                src={printingFlowVideo}
                autoPlay
                loop
                muted
                playsInline
              />
            </li>
            <li>
              <span className="freshmark-project__list-label">Device management</span>
              Bluetooth printer pairing, paper size detection, and test prints.
            </li>
            <li>
              <span className="freshmark-project__list-label">Reporting</span>
              Print history is stored locally and synced back to the admin dashboard.
            </li>
          </ul>
        </div>
      </div>
    </section>
  </main>
  );
};
